import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { api } from '../../services/api';
import ExportBookingsCSV from './ExportBookings';
import SortableTable from './SortableTable';
import { getBookingEndpoint, getBookingDateKeyAndLabel } from './bookingTypeUtils';

const BookingPagination = ({ operators, operatorId, bookingType }) => {
  const [persistedOperatorId, setPersistedOperatorId] = useState(operatorId);
  const [bookings, setBookings] = useState([]);
  const [sortedBookings, setSortedBookings] = useState([]); // Store sorted data
  const [pageSize, setPageSize] = useState(25);
  const [lastKey, setLastKey] = useState(null);
  const [previousKeys, setPreviousKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const { dateLabel } = getBookingDateKeyAndLabel(bookingType);
  const bookingTypeLabel = dateLabel;
  const exportButtonText = `Export ${bookingTypeLabel} Bookings (Operator ${persistedOperatorId})`;

  useEffect(() => {
      setPersistedOperatorId(operatorId);
      if (operatorId && operatorId !== "") {
        fetchBookings();
      }
    }, [operatorId, pageSize, bookingType]);

    const fetchBookings = async (key = null) => {
      setLoading(true);
      // Use operatorId prop directly for consistency
      const partitionKey = operatorId;
      const params = { partitionKey, pageSize };
      if (key) {
        params.paginationKeyCursor = key;
      }
      const queryString = new URLSearchParams(params).toString();
      const config = {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
        }
      };

      try {
        const endpoint = getBookingEndpoint(bookingType, true);
        const url = `${endpoint}?${queryString}`;
        const response = await api.get(url, config);
        if (response.data.statusCode === 200 && response.data.errorMessage == null) {
          setBookings(response.data.items);
          setSortedBookings(response.data.items); // Initialize sorted data
          if (key && !previousKeys.includes(key)) {
            setPreviousKeys(prev => [...prev, key]);
          }
          setLastKey(response.data.paginationKeyCursor || null);
        } else {
          console.error("Error fetching bookings:", response.data.errorMessage);
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    const handlePrevious = () => {
      if (previousKeys.length) {
        const prevKeys = [...previousKeys];
        prevKeys.pop();
        const prevKey = prevKeys.length ? prevKeys[prevKeys.length - 1] : null;
        setPreviousKeys(prevKeys);
        fetchBookings(prevKey);
      }
    };

    const handleNext = () => {
      if (lastKey) {
        fetchBookings(lastKey);
      }
    };


    // Stable callback to prevent infinite loops
    const handleSortedData = useCallback((sorted) => {
      setSortedBookings(sorted);
    }, []);

    const operator = operators?.find(op => op.operatorId === persistedOperatorId);
    const filteredOperators = persistedOperatorId ? [operator].filter(Boolean) : [];

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label htmlFor="page-size">Page Size: </label>
            <select id="page-size" onChange={e => setPageSize(+e.target.value)} value={pageSize}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <ExportBookingsCSV
            operators={filteredOperators}
            bookings={sortedBookings} // Export sorted data
            bookingType={bookingType}
            buttonText={exportButtonText}
            disabled={!persistedOperatorId || loading}
          />
        </div>

        {/* Use SortableTable component */}
        <SortableTable
          data={bookings}
          bookingType={bookingType}
          showQuickSort={true}
          onDataChange={handleSortedData}
          loading={loading}
          showOperatorColumn={false} // Hide operator column in paginated view
        />

        <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button className="themed-button" onClick={handlePrevious} disabled={!previousKeys.length}>
            Previous
          </button>
          <span>Showing {sortedBookings.length} records</span>
          <button className="themed-button" onClick={handleNext} disabled={!lastKey}>
            Next
          </button>
        </div>
      </div>
    );
  };

  BookingPagination.propTypes = {
    operators: PropTypes.array.isRequired,
    operatorId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    bookingType: PropTypes.string.isRequired,
  };

  export default BookingPagination;